import type { Vehicle } from '../services/vehicle.service'
import type { VehicleEquipmentPreset } from './vehicleEquipmentPresets'
import { applyPresetToVehicleVisualizer } from './vehicleEquipmentHelper'
import { vehicleInteriorVolume } from './vehicleFormContract'

export function applyPresetToForm(formData: Partial<Vehicle>, preset: VehicleEquipmentPreset): Partial<Vehicle> & { isTractor: boolean } {
  const dimensions = {
    length: preset.dimensions?.length ?? formData.length ?? null,
    width: preset.dimensions?.width ?? formData.width ?? null,
    height: preset.dimensions?.height ?? formData.height ?? null,
  }
  const currentVehicleObj = {
    ...formData,
    dimensions: {
      length: formData.length ?? null,
      width: formData.width ?? null,
      height: formData.height ?? null,
    },
    target_cg_x: formData.target_cg_x ?? 0.5,
    target_cg_y: formData.target_cg_y ?? 0.5,
    constraintEngineConfig: {
      ...formData.constraintEngineConfig,
      targetCG: {
        x: formData.target_cg_x ?? 0.5,
        y: formData.target_cg_y ?? 0.5,
        z: (formData as any).constraintEngineConfig?.targetCG?.z ?? 0.5
      }
    }
  }

  const updated = applyPresetToVehicleVisualizer(currentVehicleObj, preset)

  // Map preset ID to vehicle_subtype
  const presetToSubtype: Record<string, string> = {
    'van_standard': 'fourgonnette_standard',
    'rigid_truck_2_axles_standard': 'porteur_2_essieux_12t',
    'rigid_truck_2_axles_short': 'porteur_2_essieux_12t',
    'rigid_truck_2_axles_long': 'porteur_2_essieux_12t',
    'rigid_truck_2_axles_extra_long': 'porteur_2_essieux_12t',
    'rigid_truck_2_axles_18t_short': 'porteur_2_essieux_18t',
    'rigid_truck_2_axles_18t_standard': 'porteur_2_essieux_18t',
    'rigid_truck_2_axles_18t_long': 'porteur_2_essieux_18t',
    'tractor_4x2': 'tracteur_4x2',
    'tractor_6x2': 'tracteur_6x2',
    'rigid_truck_3_axles_standard': 'porteur_3_essieux_26t',
    'rigid_truck_3_axles_long': 'porteur_3_essieux_26t',
    'semi_trailer_2_axles': 'semi_remorque_2_essieux_34t',
    'semi_trailer_3_axles': 'semi_remorque_3_essieux_44t',
    'trailer_2_axles': 'remorque_2_essieux',
    'trailer_3_axles': 'remorque_3_essieux',
    // Conteneurs - mapping complet
    'container_20ft': 'dry_20',
    'container_40ft': 'dry_40',
    'container_45ft_high_cube': 'high_cube_45',
    'container_40ft_high_cube': 'high_cube_40',
  }

  const prev = formData
  return {
    ...prev,
    type: updated.type ?? prev.type,
    vehicle_subtype: (presetToSubtype[preset.id] || prev.vehicle_subtype) as Vehicle['vehicle_subtype'],
    container_type: (preset.vehicleType === 'CONTAINER') ? (presetToSubtype[preset.id] || prev.container_type) as Vehicle['container_type'] : null,
    length: dimensions.length,
    width: dimensions.width,
    height: dimensions.height,
    capacity_volume: preset.category === 'tracteur' ? null : vehicleInteriorVolume(dimensions.length, dimensions.width, dimensions.height),
    capacity_weight: updated.capacity_weight !== undefined ? updated.capacity_weight : prev.capacity_weight,
    empty_weight: preset.emptyWeight ?? prev.empty_weight,
    max_weight_permitted: updated.max_weight_permitted !== undefined ? updated.max_weight_permitted : prev.max_weight_permitted,
    max_combination_weight: updated.max_combination_weight !== undefined ? updated.max_combination_weight : prev.max_combination_weight,
    mass_contract_version: updated.mass_contract_version !== undefined ? updated.mass_contract_version : prev.mass_contract_version,
    mass_provenance: updated.mass_provenance !== undefined ? updated.mass_provenance : prev.mass_provenance,
    max_floor_pressure: updated.max_floor_pressure !== undefined ? updated.max_floor_pressure : prev.max_floor_pressure,
    pressure_cell_target_size: updated.pressure_cell_target_size !== undefined ? updated.pressure_cell_target_size : prev.pressure_cell_target_size,
    has_reefer_unit: updated.has_reefer_unit !== undefined ? updated.has_reefer_unit : prev.has_reefer_unit,
    reefer_temperature_range: updated.reefer_temperature_range !== undefined ? updated.reefer_temperature_range : prev.reefer_temperature_range,
    is_hazardous_compatible: updated.is_hazardous_compatible !== undefined ? updated.is_hazardous_compatible : prev.is_hazardous_compatible,
    max_cg_deviation: updated.max_cg_deviation ?? prev.max_cg_deviation,
    target_cg_x: updated.target_cg_x ?? prev.target_cg_x,
    target_cg_y: updated.target_cg_y ?? prev.target_cg_y,
    // Pour les conteneurs, ne pas créer de configuration d'essieux
    axle_configuration: (updated.type === 'CONTAINER' || preset.vehicleType === 'CONTAINER') ? null : (updated.axle_configuration ?? prev.axle_configuration),
    // Pour les tracteurs, effacer les obstacles car ils ne sont pas applicables
    obstacles: preset.category === 'tracteur' ? [] : (updated.obstacles ?? prev.obstacles),
    // Pour les conteneurs et véhicules sans sellette, forcer fifth_wheel à null
    fifth_wheel: (updated.type === 'CONTAINER' || preset.vehicleType === 'CONTAINER') ? null : (preset.fifthWheel ? preset.fifthWheel : null),
    constraintEngineConfig: updated.constraintEngineConfig ?? prev.constraintEngineConfig,
    // Propriété temporaire pour détecter si c'est un tracteur (UI only)
    isTractor: preset.category === 'tracteur'
  }
}

